/**
 * Authorized HTTP Client
 * Axios instance with automatic token injection
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import { refreshTokens } from "../okta";
import { tokenStorage } from "../storage";

let httpClient: AxiosInstance | null = null;

export interface HttpClientConfig {
  /** Base URL for API requests */
  baseURL: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Additional headers */
  headers?: Record<string, string>;
}

/**
 * Initialize the authorized HTTP client
 */
export function initHttpClient(config: HttpClientConfig): AxiosInstance {
  httpClient = axios.create({
    baseURL: config.baseURL,
    timeout: config.timeout ?? 30000,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
  });

  // Request interceptor - add auth token
  httpClient.interceptors.request.use(
    async (axiosConfig: InternalAxiosRequestConfig) => {
      const tokens = await tokenStorage.getTokens();

      if (tokens?.accessToken) {
        axiosConfig.headers.Authorization = `${tokens.tokenType} ${tokens.accessToken}`;
      }

      return axiosConfig;
    },
    (error) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
  );

  // Response interceptor - handle token refresh
  httpClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If 401 and we haven't retried yet, try to refresh token
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newTokens = await refreshTokens();

          if (newTokens && httpClient) {
            originalRequest.headers.Authorization = `${newTokens.tokenType} ${newTokens.accessToken}`;
            return httpClient(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          // Clear tokens and let the app handle logout
          await tokenStorage.clearTokens();
        }
      }

      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  return httpClient;
}

/**
 * Get the authorized HTTP client
 */
export function getHttpClient(): AxiosInstance {
  if (!httpClient) {
    throw new Error("HTTP client not initialized. Call initHttpClient first.");
  }
  return httpClient;
}

/**
 * Make an authorized HTTP request
 */
export async function authorizedHttp<T>(
  config: AxiosRequestConfig,
): Promise<{ data: T }> {
  const client = getHttpClient();
  return client.request<T>(config);
}

/**
 * HTTP request helper for React Query / Orval
 */
export function authorizedHttpRequest<T>(
  config: AxiosRequestConfig,
): Promise<T> {
  const source = axios.CancelToken.source();
  const promise = authorizedHttp<T>({
    ...config,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // @ts-expect-error - adding cancel method to the promise
  promise.cancel = () => {
    source.cancel("Query was cancelled by React Query");
  };

  return promise;
}
